import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { CmCoreModule } from '@ats/cm/core';
import { AtsIconsModule } from '@ats/icons';
import { AtsUiModule } from '@ats/ui';
import { AtsUtilityModule } from '@ats/utility';
import { AccessibilityModule } from '@cm/shared/ui/accessibility';
import { CmAutocompleteComponent } from '@cm/shared/ui/autocomplete';
import { CmMiniButtonDirective } from '@cm/shared/ui/button';
import { LocalizeComponent, LocalizePipe } from '@cm/ui/localize';
import { EditorModule } from '@tinymce/tinymce-angular';
import { CmAddressFormComponent } from './components/cm-address-form/cm-address-form.component';
import { CmAnnouncementDetailComponent } from './components/cm-announcement-detail/cm-announcement-detail.component';
import { CmCertLogicTreeComponent } from './components/cm-cert-logic-tree/cm-cert-logic-tree.component';
import { CmCertLogicViewComponent } from './components/cm-cert-logic-tree/cm-cert-logic-view.component';
import { CmContextOverlayComponent } from './components/cm-context-overlay/context-overlay.component';
import { CmContinueModalComponent } from './components/cm-continue-modal/cm-continue-modal.component';
import { CmCookieModalComponent } from './components/cm-cookie/cookie-modal.component';
import { CmCreqUploadComponent } from './components/cm-creq-upload/creq-upload.component';
import { CmCreqUploadService } from './components/cm-creq-upload/creq-upload.service';
import { CmSelectionFormComponent } from './components/cm-creq-upload/selection-form/selection-form.component';
import { CmSelectionListComponent } from './components/cm-creq-upload/selection-list/selection-list.component';
import { CmUploadConfirmationComponent } from './components/cm-creq-upload/upload-confirmation/upload-confirmation.component';
import { CmUploadFormComponent } from './components/cm-creq-upload/upload-form/upload-form.component';
import { CmDashComponent } from './components/cm-dash/cm-dash.component';
import { CmDeleteConfirmationComponent } from './components/cm-delete-confirmation/cm-delete-confirmation.component';
import { CmFileUploadComponent } from './components/cm-file-upload/cm-file-upload.component';
import { CmInlineAlert } from './components/cm-inline-alert/inline-alert.component';
import { CmKeyValListComponent } from './components/cm-key-val/key-val-list.component';
import { CmKeyValComponent } from './components/cm-key-val/key-val.component';
import { CmLoaderComponent } from './components/cm-loader/cm-loader.component';
import { CmLoadingDialogComponent } from './components/cm-loading-dialog/cm-loading-dialog.component';
import { CmLoadingIconComponent } from './components/cm-loading-icon/cm-loading-icon.component';
import { CmLocalizeModalComponent } from './components/cm-localize-modal/localize-modal.component';
import { CmMultiselectModalComponent } from './components/cm-multiselect-modal/cm-multiselect-modal.component';
import { CmMultiselectComponent } from './components/cm-multiselect/cm-multiselect.component';
import { CmNotFoundComponent } from './components/cm-not-found/cm-not-found.component';
import { CmNotificationsComponent } from './components/cm-notifications/cm-notifications.component';
import { CmSnackBarA11yComponent } from './components/cm-notifications/sm-snackbar-a11y/cm-snackbar-a11y.component';
import { CmProgressIndicatorComponent } from './components/cm-progress-indicator/cm-progress-indicator.component';
import { CmReloadConfirmationComponent } from './components/cm-reload-confirmation/cm-reload-confirmation.component';
import { CmSearchComponent } from './components/cm-search/cm-search.component';
import { CmSelectSupportedLanguageComponent } from './components/cm-select-supported-language/cm-select-supported-lang.component';
import { CmSupportedCultureMenuComponent } from './components/cm-supported-culture-menu/cm-supported-culture-menu.component';
import { CmTableCellComponent } from './components/cm-table/cm-table-cell/cm-table-cell.component';
import { CmTableRowComponent } from './components/cm-table/cm-table-row/cm-table-row.component';
import { CmTableComponent } from './components/cm-table/cm-table.component';
import { CmTimeoutModalComponent } from './components/cm-timeout/cm-timeout-modal/cm-timeout-modal.component';
import { CmTimeoutComponent } from './components/cm-timeout/cm-timeout.component';
import { CmUIBugComponent } from './components/cm-ui-bug/ui-bug.component';
import { CmUnsavedConfirmationComponent } from './components/cm-unsaved-confirmation/cm-unsaved-confirmation.component';
import {
	CmCSVUploadDirective,
	CmDataLabelDirective,
	CmInnerHtmlSpacingDirective,
	CmInternalizeLinkDirective,
	CmLinkButtonDirective,
	CmLocalizeDirective,
	CmLogFormDirective,
	CmSSODirective,
	CmSelectFullDirective,
	CmVerticalStepperScrollerDirective,
	CmWrapButtonDirective,
} from './directives';
import { CmContextOverlayDirective } from './directives/cm-context-overlay.directive';
import { CmCurrentLinkDirective } from './directives/cm-current-link.directive';
import { CmFileDownloadDirective } from './directives/cm-file-download.directive';
import { CmLinkDescriptionDirective } from './directives/cm-link-description.directive';
import { CmMarkAsteriskDirective } from './directives/cm-mark-asterisk.directive';
import { CmDatePipe, CmLinkFromRootPipe, CmLocalizeEnumPipe, CmTimePipe } from './pipes';
import { CmCulturePipe } from './pipes/culture.pipe';
import { CmSecureImagePipe } from './pipes/secure-image.pipe';
import { CmReloadService } from './services/cm-reload.service';
import { CmRouteService } from './services/route.service';

const declarations = [
	CmAddressFormComponent,
	CmAnnouncementDetailComponent,
	CmTimeoutModalComponent,
	CmTimeoutComponent,
	CmCSVUploadDirective,
	CmCertLogicTreeComponent,
	CmCertLogicViewComponent,
	CmContextOverlayComponent,
	CmContextOverlayDirective,
	CmContinueModalComponent,
	CmCookieModalComponent,
	CmCreqUploadComponent,
	CmCulturePipe,
	CmDashComponent,
	CmDatePipe,
	CmDeleteConfirmationComponent,
	CmFileDownloadDirective,
	CmFileUploadComponent,
	CmInlineAlert,
	CmInnerHtmlSpacingDirective,
	CmVerticalStepperScrollerDirective,
	CmKeyValComponent,
	CmKeyValListComponent,
	CmLinkButtonDirective,
	CmLinkFromRootPipe,
	CmLoaderComponent,
	CmLoadingIconComponent,
	CmLoadingDialogComponent,
	CmLocalizeDirective,
	CmLocalizeEnumPipe,
	CmLocalizeModalComponent,
	CmLogFormDirective,
	CmMarkAsteriskDirective,
	CmNotFoundComponent,
	CmNotificationsComponent,
	CmReloadConfirmationComponent,
	CmMultiselectComponent,
	CmInternalizeLinkDirective,
	CmProgressIndicatorComponent,
	CmSSODirective,
	CmSecureImagePipe,
	CmSelectFullDirective,
	CmSelectSupportedLanguageComponent,
	CmSelectionFormComponent,
	CmSelectionListComponent,
	CmSupportedCultureMenuComponent,
	CmTableCellComponent,
	CmTableComponent,
	CmTableRowComponent,
	CmDataLabelDirective,
	CmTimePipe,
	CmUIBugComponent,
	CmUnsavedConfirmationComponent,
	CmUploadConfirmationComponent,
	CmUploadFormComponent,
	CmInnerHtmlSpacingDirective,
	CmCurrentLinkDirective,
	CmLinkDescriptionDirective,
	CmSearchComponent,
	CmMultiselectModalComponent,
	CmSnackBarA11yComponent,
	CmWrapButtonDirective,
];

const publicImports = [
	CommonModule,
	AtsUiModule,
	CmCoreModule,
	FormsModule,
	ReactiveFormsModule,
	RouterModule,
	AtsIconsModule,
	AtsUtilityModule,
	MatDialogModule,
	CmAutocompleteComponent,
	AccessibilityModule,
];

const privateImports = [CmMiniButtonDirective, EditorModule, LocalizeComponent, LocalizePipe];

const providers = [
	CmDatePipe,
	CmCulturePipe,
	CmTimePipe,
	CmLinkFromRootPipe,
	CmLocalizeEnumPipe,
	CmCreqUploadService,
	CmSecureImagePipe,
	CmReloadService,
];

@NgModule({
	declarations,
	imports: [...publicImports, ...privateImports],
	exports: [...publicImports, ...declarations],
	providers,
})
export class CmUiModule {
	constructor(
		private readonly routeService: CmRouteService,
		private readonly reload: CmReloadService
	) {}
}
